/**
 * Per-page Provider for the Wholesale Workspace pages.
 * Exposes session, file actions, apply/commit, runs history + detail, and
 * cleared-families state. Each Wholesale page wraps itself in a
 * <WholesaleProvider> — independent of the others, matching the existing
 * PhotoListProvider scoping.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"

import {
  WholesaleClearedFamily,
  WholesaleFileSummary,
  WholesaleRunDetail,
  WholesaleRunSummary,
  WholesaleSession,
  WholesaleVendor,
  addClearedFamily as apiAddClearedFamily,
  completeRun as apiCompleteRun,
  createDraft as apiCreateDraft,
  deleteFile as apiDeleteFile,
  excludeVehicle as apiExcludeVehicle,
  getLatestDraft as apiGetLatestDraft,
  getRun as apiGetRun,
  getSession as apiGetSession,
  listClearedFamilies as apiListClearedFamilies,
  listRuns as apiListRuns,
  reassignVendor as apiReassignVendor,
  rebucketDraft as apiRebucketDraft,
  removeClearedFamily as apiRemoveClearedFamily,
  reopenRun as apiReopenRun,
  restoreVehicle as apiRestoreVehicle,
  selectCandidate as apiSelectCandidate,
  uploadFile as apiUploadFile,
} from "../api/wholesale"
import { useDealerGroup } from "./DealerGroupContext"

interface WholesaleContextValue {
  // Session (current uploaded files)
  session: WholesaleSession | null
  sessionLoading: boolean
  sessionError: string | null
  refreshSession: () => Promise<void>

  // File actions
  uploadFile: (file: File, overrideVendor?: WholesaleVendor) => Promise<WholesaleFileSummary>
  reassignVendor: (fileId: number, vendor: WholesaleVendor) => Promise<WholesaleFileSummary>
  deleteFile: (fileId: number) => Promise<void>
  fileActionLoading: boolean
  fileActionError: string | null

  // Draft lifecycle (the working sheet). createDraft/rebucket/select/
  // complete/reopen all resolve into `runDetail`; `actionLoading`/
  // `actionError` cover any in-flight mutation.
  runDetail: WholesaleRunDetail | null
  loadRunDetail: (runId: number) => Promise<void>
  resumeLatestDraft: () => Promise<void>
  createDraft: (families: string[], perValue: Record<string, boolean> | null) => Promise<WholesaleRunDetail | null>
  rebucketDraft: (runId: number, families: string[], perValue: Record<string, boolean> | null) => Promise<void>
  selectCandidate: (runId: number, vehicleId: number, tekionStock: string) => Promise<void>
  excludeVehicle: (runId: number, vehicleId: number) => Promise<void>
  restoreVehicle: (runId: number, vehicleId: number) => Promise<void>
  completeRun: (runId: number) => Promise<void>
  reopenRun: (runId: number) => Promise<void>
  clearRunDetail: () => void
  runDetailLoading: boolean
  runDetailError: string | null
  actionLoading: boolean
  actionError: string | null

  // Runs list (switcher + Reports page)
  runs: WholesaleRunSummary[]
  runsLoading: boolean
  runsError: string | null
  refreshRuns: () => Promise<void>

  // Cleared families (Preferences page + filter panel)
  clearedFamilies: WholesaleClearedFamily[]
  clearedFamiliesLoading: boolean
  clearedFamiliesError: string | null
  refreshClearedFamilies: () => Promise<void>
  addClearedFamily: (family: string) => Promise<void>
  removeClearedFamily: (family: string) => Promise<void>
}

const WholesaleContext = createContext<WholesaleContextValue | undefined>(undefined)

export const WholesaleProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Working surface: runs are scoped to the active group; a concrete group is
  // required to commit (the page shows a notice under "All Groups").
  const { dealerGroupId } = useDealerGroup()

  // ---- Session ----
  const [session, setSession] = useState<WholesaleSession | null>(null)
  const [sessionLoading, setSessionLoading] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)

  // ---- File actions ----
  const [fileActionLoading, setFileActionLoading] = useState(false)
  const [fileActionError, setFileActionError] = useState<string | null>(null)

  // ---- Draft lifecycle ----
  const [actionLoading, setActionLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // ---- Runs list ----
  const [runs, setRuns] = useState<WholesaleRunSummary[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [runsError, setRunsError] = useState<string | null>(null)
  const [runDetail, setRunDetail] = useState<WholesaleRunDetail | null>(null)
  const [runDetailLoading, setRunDetailLoading] = useState(false)
  const [runDetailError, setRunDetailError] = useState<string | null>(null)

  // ---- Cleared families ----
  const [clearedFamilies, setClearedFamilies] = useState<WholesaleClearedFamily[]>([])
  const [clearedFamiliesLoading, setClearedFamiliesLoading] = useState(false)
  const [clearedFamiliesError, setClearedFamiliesError] = useState<string | null>(null)

  // ---------------------------------------------------------------------------
  // Session
  // ---------------------------------------------------------------------------

  const refreshSession = useCallback(async () => {
    setSessionLoading(true)
    setSessionError(null)
    try {
      const data = await apiGetSession()
      setSession(data)
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : "Failed to load session")
    } finally {
      setSessionLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await apiGetSession()
        if (!cancelled) setSession(data)
      } catch (err) {
        if (!cancelled) {
          setSessionError(err instanceof Error ? err.message : "Failed to load session")
        }
      } finally {
        if (!cancelled) setSessionLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // ---------------------------------------------------------------------------
  // File actions — each action refreshes the session so consumers re-render
  // from the server's view of truth, not local optimism.
  // ---------------------------------------------------------------------------

  const uploadFile = useCallback(
    async (file: File, overrideVendor?: WholesaleVendor) => {
      setFileActionLoading(true)
      setFileActionError(null)
      try {
        const summary = await apiUploadFile(file, overrideVendor)
        await refreshSession()
        return summary
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed"
        setFileActionError(message)
        throw err
      } finally {
        setFileActionLoading(false)
      }
    },
    [refreshSession],
  )

  const reassignVendor = useCallback(
    async (fileId: number, vendor: WholesaleVendor) => {
      setFileActionLoading(true)
      setFileActionError(null)
      try {
        const summary = await apiReassignVendor(fileId, vendor)
        await refreshSession()
        return summary
      } catch (err) {
        const message = err instanceof Error ? err.message : "Reassign failed"
        setFileActionError(message)
        throw err
      } finally {
        setFileActionLoading(false)
      }
    },
    [refreshSession],
  )

  const deleteFile = useCallback(
    async (fileId: number) => {
      setFileActionLoading(true)
      setFileActionError(null)
      try {
        await apiDeleteFile(fileId)
        await refreshSession()
      } catch (err) {
        const message = err instanceof Error ? err.message : "Delete failed"
        setFileActionError(message)
        throw err
      } finally {
        setFileActionLoading(false)
      }
    },
    [refreshSession],
  )

  // ---------------------------------------------------------------------------
  // Draft lifecycle — all mutations resolve into `runDetail`.
  // ---------------------------------------------------------------------------

  const clearRunDetail = useCallback(() => {
    setRunDetail(null)
    setRunDetailError(null)
  }, [])

  const createDraft = useCallback(
    async (families: string[], perValue: Record<string, boolean> | null) => {
      setActionLoading(true)
      setActionError(null)
      try {
        const detail = await apiCreateDraft({ families, per_value: perValue }, dealerGroupId)
        setRunDetail(detail)
        await refreshSession()
        return detail
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to start run")
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [dealerGroupId, refreshSession],
  )

  const rebucketDraft = useCallback(
    async (runId: number, families: string[], perValue: Record<string, boolean> | null) => {
      setActionLoading(true)
      setActionError(null)
      try {
        const detail = await apiRebucketDraft(runId, { families, per_value: perValue })
        setRunDetail(detail)
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to apply filter")
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [],
  )

  const selectCandidate = useCallback(
    async (runId: number, vehicleId: number, tekionStock: string) => {
      setActionLoading(true)
      setActionError(null)
      try {
        const detail = await apiSelectCandidate(runId, vehicleId, tekionStock)
        setRunDetail(detail)
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to select")
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [],
  )

  const excludeVehicle = useCallback(
    async (runId: number, vehicleId: number) => {
      setActionLoading(true)
      setActionError(null)
      try {
        setRunDetail(await apiExcludeVehicle(runId, vehicleId))
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to exclude")
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [],
  )

  const restoreVehicle = useCallback(
    async (runId: number, vehicleId: number) => {
      setActionLoading(true)
      setActionError(null)
      try {
        setRunDetail(await apiRestoreVehicle(runId, vehicleId))
      } catch (err) {
        setActionError(err instanceof Error ? err.message : "Failed to restore")
        throw err
      } finally {
        setActionLoading(false)
      }
    },
    [],
  )

  const completeRun = useCallback(async (runId: number) => {
    setActionLoading(true)
    setActionError(null)
    try {
      await apiCompleteRun(runId)
      setRunDetail(await apiGetRun(runId))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to complete")
      throw err
    } finally {
      setActionLoading(false)
    }
  }, [])

  const reopenRun = useCallback(async (runId: number) => {
    setActionLoading(true)
    setActionError(null)
    try {
      await apiReopenRun(runId)
      setRunDetail(await apiGetRun(runId))
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to reopen")
      throw err
    } finally {
      setActionLoading(false)
    }
  }, [])

  const resumeLatestDraft = useCallback(async () => {
    if (dealerGroupId === null) return
    setRunDetailLoading(true)
    setRunDetailError(null)
    try {
      setRunDetail(await apiGetLatestDraft(dealerGroupId))
    } catch (err) {
      setRunDetailError(err instanceof Error ? err.message : "Failed to resume draft")
    } finally {
      setRunDetailLoading(false)
    }
  }, [dealerGroupId])

  // ---------------------------------------------------------------------------
  // Runs list
  // ---------------------------------------------------------------------------

  const refreshRuns = useCallback(async () => {
    if (dealerGroupId === null) {
      setRuns([])
      setRunsLoading(false)
      return
    }
    setRunsLoading(true)
    setRunsError(null)
    try {
      const data = await apiListRuns(50, dealerGroupId)
      setRuns(data.runs)
    } catch (err) {
      setRunsError(err instanceof Error ? err.message : "Failed to load runs")
    } finally {
      setRunsLoading(false)
    }
  }, [dealerGroupId])

  const loadRunDetail = useCallback(async (runId: number) => {
    setRunDetailLoading(true)
    setRunDetailError(null)
    try {
      const data = await apiGetRun(runId)
      setRunDetail(data)
    } catch (err) {
      setRunDetailError(err instanceof Error ? err.message : "Failed to load run detail")
    } finally {
      setRunDetailLoading(false)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Cleared families
  // ---------------------------------------------------------------------------

  const refreshClearedFamilies = useCallback(async () => {
    if (dealerGroupId === null) {
      setClearedFamilies([])
      setClearedFamiliesLoading(false)
      return
    }
    setClearedFamiliesLoading(true)
    setClearedFamiliesError(null)
    try {
      const data = await apiListClearedFamilies(dealerGroupId)
      setClearedFamilies(data.families)
    } catch (err) {
      setClearedFamiliesError(
        err instanceof Error ? err.message : "Failed to load cleared families",
      )
    } finally {
      setClearedFamiliesLoading(false)
    }
  }, [dealerGroupId])

  const addClearedFamily = useCallback(
    async (family: string) => {
      await apiAddClearedFamily(family, dealerGroupId)
      await refreshClearedFamilies()
    },
    [refreshClearedFamilies, dealerGroupId],
  )

  const removeClearedFamily = useCallback(
    async (family: string) => {
      await apiRemoveClearedFamily(family, dealerGroupId)
      await refreshClearedFamilies()
    },
    [refreshClearedFamilies, dealerGroupId],
  )

  // ---------------------------------------------------------------------------
  // Value
  // ---------------------------------------------------------------------------

  const value = useMemo<WholesaleContextValue>(
    () => ({
      session,
      sessionLoading,
      sessionError,
      refreshSession,
      uploadFile,
      reassignVendor,
      deleteFile,
      fileActionLoading,
      fileActionError,
      runDetail,
      loadRunDetail,
      resumeLatestDraft,
      createDraft,
      rebucketDraft,
      selectCandidate,
      excludeVehicle,
      restoreVehicle,
      completeRun,
      reopenRun,
      clearRunDetail,
      runDetailLoading,
      runDetailError,
      actionLoading,
      actionError,
      runs,
      runsLoading,
      runsError,
      refreshRuns,
      clearedFamilies,
      clearedFamiliesLoading,
      clearedFamiliesError,
      refreshClearedFamilies,
      addClearedFamily,
      removeClearedFamily,
    }),
    [
      session, sessionLoading, sessionError, refreshSession,
      uploadFile, reassignVendor, deleteFile, fileActionLoading, fileActionError,
      runDetail, loadRunDetail, resumeLatestDraft, createDraft, rebucketDraft,
      selectCandidate, excludeVehicle, restoreVehicle, completeRun, reopenRun, clearRunDetail,
      runDetailLoading, runDetailError, actionLoading, actionError,
      runs, runsLoading, runsError, refreshRuns,
      clearedFamilies, clearedFamiliesLoading, clearedFamiliesError,
      refreshClearedFamilies, addClearedFamily, removeClearedFamily,
    ],
  )

  return <WholesaleContext.Provider value={value}>{children}</WholesaleContext.Provider>
}

export const useWholesale = (): WholesaleContextValue => {
  const ctx = useContext(WholesaleContext)
  if (!ctx) {
    throw new Error("useWholesale must be used within WholesaleProvider")
  }
  return ctx
}
